/**
 * LandingPageEditor — the doctor's single-page control centre for
 * their public `/dr/:slug` microsite. iter100.32 (2026-02-28) merged
 * the old two-page bounce (MyLandingPage → "Customize page" →
 * LandingPageEditor) into a single tabbed surface:
 *
 *   • Overview       — Public URL, QR code, funnel counters, content library summary
 *   • Design & Story — Cover, tagline, story, clinic photos, contact CTAs, trust chips
 *   • Articles       — Educational content editor (auto-publishes)
 *   • Case Studies   — Before/after transformations (needs Invisique approval)
 *
 * All identity fields save on blur / on click — no monolithic "Publish"
 * button. Analytics counters refresh every 60s in the background.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    ArrowSquareOut, Camera, ImageSquare, Plus, Trash, CheckCircle,
    WhatsappLogo, Eye, Sparkle, Newspaper, PaintBrush,
    ChatCircleText, Clock, PaperPlaneTilt, WarningCircle,
    ArrowRight, Info, Star, ShieldCheck, ShareNetwork,
    SealCheck, Certificate, Stethoscope, GraduationCap, Translate,
    Buildings, MapPin,
    // iter100.42 — Extra Phosphor icons for the Trust Chips system.
    Trophy, UsersFour, Baby, Lightning, Wheelchair, GenderFemale,
    FirstAid, House, CreditCard, Rainbow, Medal, ScanSmiley, CalendarBlank,
    Calendar, Copy, LinkSimple,
    // iter100.58 — Practice-attribute additions: parking / virtual consults /
    // late evening / washroom / refreshments / free wifi.
    VideoCamera, Car, Toilet, Coffee, WifiHigh, Moon,
    // iter100.46d — Overview + Topics tabs.
    Gauge, Lightbulb, FileText, PenNib, ArrowUpRight, ArrowClockwise,
} from "@phosphor-icons/react";
import ContentEditor from "@/components/doctor/ContentEditor";
import SuggestedTopicsPanel from "@/components/doctor/SuggestedTopicsPanel";
import OverviewTab from "@/components/doctor/OverviewTab";
import ShareProfileModal from "@/components/site/ShareProfileModal";

/* iter100.9 — Doctor-side API endpoints for the Articles / Case Studies
   editors. The same ContentEditor is reused by the admin portal with a
   different `apiBase`. */
const DOCTOR_CONTENT_API = {
    list:      (kind) => `/doctor/content/${kind}`,
    createUrl: (kind) => `/doctor/content/${kind}`,
    itemUrl:   (id)   => `/doctor/content/item/${id}`,
    assetUrl:  ()     => `/doctor/content/asset`,
    submitUrl: (id)   => `/doctor/content/item/${id}/submit`,
};

const EDITOR_TABS = [
    // iter100.46d — Overview is the new default landing view. It answers
    // "is my public page working for me?" at a glance (health cards,
    // pipeline, next-best-actions) before the doctor drops into any
    // editing surface below.
    { id: "overview",     label: "Overview",       icon: Gauge },
    { id: "design",       label: "Design & Story", icon: PaintBrush },
    // iter100.46d — Topics is its own tab (was previously a panel above
    // the Articles editor). Cleaner separation: each tab has one job.
    { id: "topics",       label: "Grow Reach",     icon: Lightbulb },
    { id: "article",      label: "Articles",       icon: Newspaper },
    { id: "case_study",   label: "Case Studies",   icon: Sparkle },
    // iter100.34 — Reviews returns as a tab (was briefly a sidebar item).
    // Verified Patient Reviews is still Coming Soon; the tab shows the
    // "how it will work" preview + auto-published sample card.
    { id: "reviews",      label: "Reviews",        icon: ChatCircleText },
];

const ACCENT_PRESETS = [
    // iter100.42 — Accent picker removed from the editor per user request.
    // The `accent` field still exists in the backend / DB and continues to
    // drive the public page's colour highlights (defaults to "teal"). Kept
    // here as a reference in case we bring the picker back as an admin tool.
    { id: "teal",  label: "Teal",   swatch: "hsl(192 100% 31%)" },
    { id: "rose",  label: "Rose",   swatch: "hsl(340 75% 45%)" },
    { id: "amber", label: "Amber",  swatch: "hsl(30 90% 45%)" },
    { id: "slate", label: "Slate",  swatch: "hsl(220 20% 30%)" },
];

// iter100.46e — Accent palette used to tint the editor identity hero
// and active tab underline so the doctor sees their chosen brand
// colour throughout the editor chrome (not just on the public page).
// Mirrors DoctorLandingPage / PublicArticlePage / OverviewTab.
const ACCENTS = {
    teal:  { base: "hsl(192 90% 33%)",  soft: "hsl(192 90% 96%)",  ring: "hsl(192 90% 88%)",  text: "hsl(192 90% 25%)" },
    rose:  { base: "hsl(340 75% 45%)",  soft: "hsl(340 75% 97%)",  ring: "hsl(340 75% 90%)",  text: "hsl(340 75% 35%)" },
    amber: { base: "hsl(28 88% 45%)",   soft: "hsl(28 88% 97%)",   ring: "hsl(28 88% 88%)",   text: "hsl(28 88% 35%)" },
    slate: { base: "hsl(215 60% 32%)",  soft: "hsl(215 60% 97%)",  ring: "hsl(215 60% 88%)",  text: "hsl(215 60% 25%)" },
};
const pickAccent = (n) => ACCENTS[n] || ACCENTS.teal;

// Compress before upload so we stay under the 5MB backend cap and
// keep the mongo doc snappy. Longest edge → maxDim, JPEG quality → 0.82.
async function compressImage(file, maxDim = 1600, quality = 0.82) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
            const w = Math.round(img.width * scale);
            const h = Math.round(img.height * scale);
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            canvas.getContext("2d").drawImage(img, 0, 0, w, h);
            resolve(canvas.toDataURL("image/jpeg", quality));
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}

// iter100.42 — Icon-name → Phosphor component map. The backend chip
// system emits string icon keys (Certificate, Star, Baby, …) so the
// frontend stays taxonomy-agnostic. Missing keys fall back to CheckCircle.
const CHIP_ICON_MAP = {
    Certificate, ShieldCheck, CheckCircle, Sparkle, Calendar,
    Star, UsersFour, Trophy, SealCheck, Medal,
    Baby, Lightning, Wheelchair, GenderFemale, FirstAid, House, CreditCard, Rainbow,
    CalendarBlank, ScanSmiley, Buildings, Translate,
    // iter100.58 — Practice-attribute additions (parking / virtual /
    // late evening / washroom / refreshments / free wifi).
    VideoCamera, Car, Toilet, Coffee, WifiHigh, Moon,
};
function ChipIcon({ name, size = 11 }) {
    const C = CHIP_ICON_MAP[name] || CheckCircle;
    return <C size={size} weight="fill" />;
}

/* iter100.42 — Client mirror of the backend's `compute_visible_chips`.
   We compute here (rather than fetch from the server) so toggling a chip
   in the editor updates the hero preview instantly without a roundtrip.
   Ordering priority must stay in sync with routers/doctor_landing_chips.py.

   iter100.191 — BUG FIX. Previously this mirror used a single
   `max_visible_chips` cap over the combined list. But since iter100.62
   the backend caps trust and practice rows INDEPENDENTLY
   (`MAX_VISIBLE_CHIPS=8` for trust · `MAX_VISIBLE_PRACTICE_CHIPS=14` for
   practice). So a doctor with 8+ trust signals would see their
   practice attributes silently truncated inside the editor preview,
   even though the public marketing microsite showed them correctly.
   Splitting the buckets before applying caps matches the backend and
   makes the editor preview finally show what patients actually see. */
function computeEffectiveChips(chipConfig, hero) {
    if (!chipConfig) return [];
    const trust = [];
    const practice = [];
    // 1. Locked auto chips. `invisique_certified` is anchored as the pill
    // next to the doctor's name in the hero — surfacing it again in the
    // chip row would be redundant. So skip it here.
    if (hero?.dci_verified) {
        trust.push({ key: "dci_verified", label: "DCI Verified", icon: "ShieldCheck", source: "auto" });
    }
    // 2. Cases treated
    const cases = chipConfig.cases_treated_count || 0;
    if (chipConfig.auto_visibility?.cases_treated && cases > 0) {
        trust.push({
            key: "cases_treated",
            label: `${cases} Invisique™ case${cases === 1 ? "" : "s"} treated`,
            icon: "CheckCircle",
            source: "auto",
        });
    }
    // 3. Admin-granted badges (visible only)
    (chipConfig.admin_badges || []).forEach((b) => {
        if (b.visible !== false) {
            trust.push({ key: b.key, label: b.label, icon: b.icon || "Medal", source: "admin" });
        }
    });
    // 4. Aligner Specialist
    if (chipConfig.auto_visibility?.aligner_specialist) {
        trust.push({ key: "aligner_specialist", label: "Aligner Specialist", icon: "Sparkle", source: "auto" });
    }
    // 5. Doctor self-attested flags (taxonomy order) — practice row.
    const flagTax = chipConfig.taxonomies?.doctor || {};
    Object.entries(flagTax).forEach(([key, spec]) => {
        if (chipConfig.doctor_flags?.[key]) {
            practice.push({ key, label: spec.label, icon: spec.icon, source: "doctor" });
        }
    });
    // 6. Auto-derived flags — only when detected AND toggled on — practice row.
    const derivedTax = chipConfig.taxonomies?.derived || {};
    Object.entries(derivedTax).forEach(([key, spec]) => {
        if (chipConfig.computed_flags?.[key] && chipConfig.doctor_flags?.[key]) {
            practice.push({ key, label: spec.label, icon: spec.icon, source: "derived" });
        }
    });
    // iter100.191 — Independent caps mirror the backend.
    const trustCap = chipConfig.max_visible_chips || 8;
    const practiceCap = chipConfig.max_visible_practice_chips || 14;
    return [...trust.slice(0, trustCap), ...practice.slice(0, practiceCap)];
}

/* iter100.42 — Compact page header that sits above the identity hero.
   Overline + subtitle line that spells out "what this page is" and
   makes the public URL discoverable at a glance (click to copy). */
function PageHeader({ slug, publicPath }) {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const fullUrl = slug ? `${origin}${publicPath}` : "";
    const shortUrl = slug ? `${origin.replace(/^https?:\/\//, "")}${publicPath}` : "";
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            toast.success("URL copied");
        } catch {
            toast.error("Couldn't copy");
        }
    };
    return (
        <div className="space-y-1" data-testid="landing-page-header">
            <div className="flex items-baseline gap-2 flex-wrap">
                <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 leading-tight">
                    My Landing Page
                </h1>
                {slug && (
                    <button
                        type="button"
                        onClick={copy}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-mono text-muted-foreground hover:text-primary transition-colors group"
                        title="Click to copy your public URL"
                        data-testid="page-header-url-copy"
                    >
                        <LinkSimple size={11} weight="bold" />
                        <span className="underline decoration-dotted underline-offset-2">{shortUrl}</span>
                        <Copy size={10} weight="regular" className="opacity-60 group-hover:opacity-100 transition-opacity" />
                    </button>
                )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
                How you show up on Invisique™ — the microsite patients see when they search for you.
            </p>
        </div>
    );
}

function buildPublicUrl(publicPath) {
    if (!publicPath) return "";
    if (typeof window === "undefined") return publicPath;
    return `${window.location.origin}${publicPath}`;
}

export default function LandingPageEditor() {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(null); // 'cover' | 'clinic' | null

    // iter100.46e — Doctor's chosen brand accent (teal / rose / amber /
    // slate). Drives the tab strip active colour + identity hero
    // highlights + Overview tab tints. Defaults to teal until state loads
    // so early renders don't flash a mismatched colour.
    const editorAccent = pickAccent(state?.accent);
    /* iter100.26 — Deep-link support. Read the `?tab=` param ONCE on
       mount and use it as initial state. From then on, we only sync
       tab → URL (never URL → tab) which avoids a nasty infinite
       loop where both useEffects fight over the URL. */
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(() => {
        const raw = searchParams.get("tab");
        if (raw && EDITOR_TABS.some((t) => t.id === raw)) return raw;
        return "overview";
    });
    // iter100.277 — Deep-link a specific *section* within a tab. When the
    // Overview health cards fire "Edit profile" / "Manage chips" /
    // "Add contact", we switch to the Design tab AND scroll to the
    // exact section (Story / Trust chips / Contact) instead of
    // dropping the doctor at the top of a long form.
    const [pendingAnchor, setPendingAnchor] = useState(null);
    useEffect(() => {
        if (!pendingAnchor) return;
        const t = setTimeout(() => {
            const el = document.getElementById(pendingAnchor);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                // Focus the first input inside the section so the
                // user's next keystroke lands where they meant to type.
                const input = el.querySelector("input, textarea, button");
                if (input && typeof input.focus === "function") {
                    input.focus({ preventScroll: true });
                }
            }
            setPendingAnchor(null);
        }, 250);
        return () => clearTimeout(t);
    }, [pendingAnchor, tab]);
    useEffect(() => {
        const current = searchParams.get("tab");
        if (current !== tab) {
            const next = new URLSearchParams(searchParams);
            next.set("tab", tab);
            setSearchParams(next, { replace: true });
        }
    }, [tab, searchParams, setSearchParams]);

    const coverInputRef = useRef(null);
    const clinicInputRef = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/doctor/landing-page/edit");
            setState(data);
        } catch {
            /* silent */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const publicPath = useMemo(
        () => (state?.slug ? `/dr/${state.slug}` : ""),
        [state?.slug],
    );

    const save = async (patch) => {
        setSaving(true);
        try {
            await api.put("/doctor/landing-page/edit", patch);
            setState((s) => ({ ...s, ...patch }));
            toast.success("Saved");
        } catch {
            toast.error("Couldn't save — try again");
        } finally {
            setSaving(false);
        }
    };

    /* iter100.42 — Trust-chip mutations. Optimistically mirror the patch
       into local state so the identity-hero chip row updates instantly.
       Server accepts partial chip_config patches (merges internally). */
    const saveChipConfig = async (patch) => {
        // Optimistic mirror BEFORE the request so the preview reacts instantly.
        setState((s) => ({
            ...s,
            chip_config: {
                ...s.chip_config,
                auto_visibility: {
                    ...(s.chip_config?.auto_visibility || {}),
                    ...(patch.auto_visibility || {}),
                },
                doctor_flags: {
                    ...(s.chip_config?.doctor_flags || {}),
                    ...(patch.doctor_flags || {}),
                },
            },
        }));
        setSaving(true);
        try {
            await api.put("/doctor/landing-page/edit", { chip_config: patch });
        } catch {
            toast.error("Couldn't save — try again");
        } finally {
            setSaving(false);
        }
    };

    /* iter100.42 — Toggle visibility of an admin-granted badge. */
    const saveBadgeVisibility = async (key, visible) => {
        setState((s) => ({
            ...s,
            chip_config: {
                ...s.chip_config,
                admin_badges: (s.chip_config?.admin_badges || []).map((b) =>
                    b.key === key ? { ...b, visible } : b
                ),
            },
        }));
        setSaving(true);
        try {
            await api.put("/doctor/landing-page/edit", { badge_visibility: { [key]: visible } });
        } catch {
            toast.error("Couldn't save — try again");
        } finally {
            setSaving(false);
        }
    };

    /* iter100.55 — Toggle a service key on / off. Client-side we keep the
       list ordered by taxonomy for a stable UI. iter100.58 — Cap removed;
       server does the final normalisation. */
    const toggleService = async (key) => {
        const current = state?.services || [];
        const taxonomyOrder = (state?.service_taxonomy || []).map((s) => s.key);
        const isOn = current.includes(key);
        const next = isOn
            ? current.filter((k) => k !== key)
            : [...current, key].sort((a, b) => taxonomyOrder.indexOf(a) - taxonomyOrder.indexOf(b));
        setState((s) => ({ ...s, services: next }));  // optimistic
        setSaving(true);
        try {
            await api.put("/doctor/landing-page/edit", { services: next });
        } catch {
            toast.error("Couldn't save — try again");
        } finally {
            setSaving(false);
        }
    };

    /* iter100.42 — Effective chip list mirrors what patients see. Recomputed
       whenever the doctor toggles anything in the Trust chips section. */
    const effectiveChips = useMemo(
        () => computeEffectiveChips(state?.chip_config, state?.hero),
        [state?.chip_config, state?.hero],
    );

    const onPickCover = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Pick an image");
        setUploading("cover");
        try {
            const data_url = await compressImage(f, 1920, 0.85);
            const { data } = await api.post("/doctor/landing-page/cover", { data_url });
            setState((s) => ({ ...s, cover: { id: data.id, data_url: data.data_url } }));
            toast.success("Cover photo updated");
        } catch {
            toast.error("Upload failed");
        } finally {
            setUploading(null);
            e.target.value = "";
        }
    };

    const removeCover = async () => {
        try {
            await api.delete("/doctor/landing-page/cover");
            setState((s) => ({ ...s, cover: null }));
            toast.success("Cover removed");
        } catch {
            toast.error("Couldn't remove cover");
        }
    };

    const onPickClinic = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const remaining = (state.limits?.clinic_photos_max || 6) - (state.clinic_photos?.length || 0);
        if (remaining <= 0) return toast.error("Already at the photo limit");
        setUploading("clinic");
        try {
            const toUpload = files.slice(0, remaining);
            for (const f of toUpload) {
                if (!f.type.startsWith("image/")) continue;
                const data_url = await compressImage(f, 1400, 0.82);
                const { data } = await api.post("/doctor/landing-page/clinic-photo", { data_url });
                setState((s) => ({
                    ...s,
                    clinic_photos: [...(s.clinic_photos || []), { id: data.id, data_url: data.data_url }],
                }));
            }
            toast.success(`Added ${toUpload.length} photo${toUpload.length === 1 ? "" : "s"}`);
        } catch {
            toast.error("One or more uploads failed");
        } finally {
            setUploading(null);
            e.target.value = "";
        }
    };

    const removeClinic = async (assetId) => {
        try {
            await api.delete(`/doctor/landing-page/clinic-photo/${assetId}`);
            setState((s) => ({
                ...s,
                clinic_photos: (s.clinic_photos || []).filter((p) => p.id !== assetId),
            }));
        } catch {
            toast.error("Couldn't remove");
        }
    };

    if (loading || !state) {
        return <div className="p-12 text-center text-muted-foreground text-sm" data-testid="landing-page-loading">Loading editor…</div>;
    }

    return (
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 space-y-6" data-testid="landing-page-editor">
            {/* iter100.42 — Page header (overline + URL subheader). Restores
                the "you are here" context we lost when iter100.41 replaced
                the plain H1 with the identity hero. Click the URL to copy. */}
            <PageHeader slug={state.slug} publicPath={publicPath} />

            {/* iter100.41 — Editor identity hero. Live mirror of the
                public hero card at /dr/{slug}: doctor photo · name ·
                INVISIQUE CERTIFIED chip · specialization · credentials ·
                clinic + city · dynamic trust chips (locked auto + admin
                grants + doctor self-attested flags, max 8). Doctors see
                EXACTLY what patients see, in the same visual language,
                right where they're editing. Preview + Share sit top-right. */}
            <EditorIdentityHero
                slug={state.slug}
                publicPath={publicPath}
                doctorName={state.doctor_name}
                clinicName={state.clinic_name}
                hero={state.hero}
                chips={effectiveChips}
                saving={saving}
                accent={editorAccent}
            />

            {/* iter100.284 — Removed `overflow-x-auto` from the tab strip.
                All 6 tabs use `flex-1 min-w-0` so they distribute evenly
                and never actually overflow — the overflow-x class was
                causing an invisible horizontal scrollbar to flicker in/
                out on tab change, giving the page a ~15px vertical
                shake. Also swapped `sticky` for a plain top-position so
                the tab strip renders in the same layer as the
                surrounding content (sticky + z-index inside a
                compositing layer was the second half of the shake). */}
            <div
                className="flex w-full bg-card border border-border rounded-xl p-1 gap-0.5 max-w-full"
                role="tablist"
                data-testid="landing-editor-tabs"
            >
                {EDITOR_TABS.map((t) => {
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            role="tab"
                            aria-selected={active}
                            type="button"
                            onClick={() => setTab(t.id)}
                            className={`flex-1 min-w-0 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                                active
                                    ? "bg-primary text-white shadow-sm"
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                            }`}
                            style={active ? { backgroundColor: editorAccent.base } : undefined}
                            data-testid={`editor-tab-${t.id}`}
                        >
                            <t.icon size={14} weight={active ? "fill" : "duotone"} />
                            <span className="truncate">{t.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* ============================
                OVERVIEW TAB (iter100.46d)
               =========================== */}
            {tab === "overview" && (
                <OverviewTab
                    state={state}
                    publicPath={publicPath}
                    onNavigateTab={(t, anchor) => {
                        setTab(t);
                        if (anchor) setPendingAnchor(anchor);
                    }}
                />
            )}

            {/* ============================
                TOPICS TAB (iter100.46d — moved out of Articles)
               =========================== */}
            {tab === "topics" && <SuggestedTopicsPanel />}

            {/* ============================
                DESIGN & STORY TAB
               =========================== */}
            {tab === "design" && (
              <>
            {/* iter100.59 — Design tab order rewritten per founder feedback:
                About you → Tagline → How patients reach you → Cover photo →
                Inside your clinic → Services offered → Trust chips. Identity
                first (story + tagline + contact reach), then visual assets,
                then curated content (services + trust). The old
                "How your landing page shows" source-map panel was removed —
                its content was duplicating the tab-specific hints. */}

            {/* ===============================================================
                ABOUT YOU (Story — single source of truth)
               ============================================================== */}
            <Section anchorId="landing-story" title="About you" hint="Your intro paragraph on the public landing page. Aim for ~150 words on why you love what you do, your approach, or what patients can expect.">
                <StoryField
                    initial={state.story || ""}
                    onSave={(v) => save({ story: v })}
                />
            </Section>

            {/* ===============================================================
                TAGLINE — sits right under About you so the two identity
                fields are edited together.
               ============================================================== */}
            <Section title="Your tagline" hint="A short quote that captures your philosophy. Shown as a pull-quote inside your About section on the public page.">
                <TaglineField
                    initial={state.tagline || ""}
                    onSave={(v) => save({ tagline: v })}
                />
            </Section>

            {/* iter100.313 — "How patients reach you" summary block was
                removed from the Design & Story tab per user request.
                Contact numbers already live on My Profile → Contact,
                and the CTA pills were duplicative here. */}

            {/* ===============================================================
                COVER PHOTO — iter100.59 compact layout: slim 16:4 dropzone
                (was 16:6, ~33% shorter) and the trailing helper text was
                consolidated into the empty-state caption to reclaim more
                vertical space.
               ============================================================== */}
            <Section title="Cover photo" hint="A wide horizontal photo of your clinic, team, or a hero portrait. Displayed edge-to-edge above your name.">
                <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={onPickCover} data-testid="editor-cover-file-input" />
                {state.cover?.data_url ? (
                    <div className="relative rounded-xl overflow-hidden border border-border bg-secondary/40">
                        <img src={state.cover.data_url} alt="Cover" className="w-full aspect-[16/4] object-cover" />
                        <div className="absolute top-2 right-2 flex gap-1.5">
                            <Button size="sm" variant="secondary" onClick={() => coverInputRef.current?.click()} data-testid="editor-cover-replace-btn">
                                <Camera size={14} /> Replace
                            </Button>
                            <Button size="sm" variant="secondary" onClick={removeCover} data-testid="editor-cover-remove-btn">
                                <Trash size={14} /> Remove
                            </Button>
                        </div>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => coverInputRef.current?.click()}
                        disabled={uploading === "cover"}
                        className="w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary/60 bg-secondary/30 flex items-center justify-center gap-3 text-muted-foreground hover:text-primary transition-colors px-4"
                        data-testid="editor-cover-upload-btn"
                    >
                        <ImageSquare size={22} weight="duotone" />
                        <div className="text-left">
                            <div className="text-sm font-semibold">{uploading === "cover" ? "Uploading…" : "Upload cover photo"}</div>
                            <div className="text-[11px] leading-tight">JPG or PNG · until then, your accent-colour gradient shows.</div>
                        </div>
                    </button>
                )}
            </Section>

            {/* ===============================================================
                CLINIC PHOTOS — iter100.59 compact: 4-col grid (was 3),
                tighter gap, smaller add-tile so the whole row fits in
                one screen even with 6 photos.
               ============================================================== */}
            <Section title="Inside your clinic" hint="Up to 6 photos of the space, chair-side, waiting area, tech. Real photos > stock.">
                <input ref={clinicInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickClinic} data-testid="editor-clinic-file-input" />
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                    {(state.clinic_photos || []).map((p) => (
                        <div key={p.id} className="relative group aspect-[4/3] rounded-lg overflow-hidden border border-border bg-secondary/40">
                            <img src={p.data_url} alt="Clinic" className="h-full w-full object-cover" />
                            <button
                                type="button"
                                onClick={() => removeClinic(p.id)}
                                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-white/95 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                                data-testid={`editor-clinic-remove-${p.id}`}
                                aria-label="Remove"
                            >
                                <Trash size={13} className="text-red-600" />
                            </button>
                        </div>
                    ))}
                    {(state.clinic_photos?.length || 0) < (state.limits?.clinic_photos_max || 6) && (
                        <button
                            type="button"
                            onClick={() => clinicInputRef.current?.click()}
                            disabled={uploading === "clinic"}
                            className="aspect-[4/3] rounded-lg border-2 border-dashed border-border hover:border-primary/60 bg-secondary/30 flex flex-col items-center justify-center gap-0.5 text-muted-foreground hover:text-primary transition-colors"
                            data-testid="editor-clinic-add-btn"
                        >
                            <Plus size={18} weight="bold" />
                            <span className="text-[11px] font-semibold">{uploading === "clinic" ? "Uploading…" : "Add photos"}</span>
                        </button>
                    )}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1.5">
                    {state.clinic_photos?.length || 0} of {state.limits?.clinic_photos_max || 6} used
                </div>
            </Section>

            {/* ===============================================================
                SERVICES OFFERED — iter100.55
                Doctor-curated picker replacing the previous hardcoded
                "Specializations" grid (which contained fake filler +
                competitor brand names). Doctor picks from a fixed
                orthodontics taxonomy; the public microsite renders only
                what's picked. Empty pick → card hides entirely.
               ============================================================== */}
            <Section
                title="Services offered"
                hint="Pick every service you actually offer — patients see only what you tick. Leave everything unticked and the card hides on your public page."
            >
                <ServicesPicker
                    taxonomy={state.service_taxonomy || []}
                    selected={state.services || []}
                    onToggle={toggleService}
                />
            </Section>

            {/* ===============================================================
                TRUST CHIPS (iter100.42, repositioned to bottom in iter100.59)
                Doctor toggles visibility on the trust chips patients see
                on their public identity card. Three buckets:
                  Auto — Invisique-managed; locked ones (Invisique
                    Certified, DCI Verified) can't be hidden.
                  Admin — Badges Invisique HQ has awarded; doctor can
                    toggle visibility but not grant/revoke.
                  Practice — Self-attested attributes (kids-friendly,
                    EMI available, etc.) plus auto-detected signals.
                Live preview: as the doctor toggles, the identity hero
                at the top of the page updates instantly.
               ============================================================== */}
            <Section
                anchorId="landing-chips"
                title="Trust chips shown on your card"
                hint={`Toggle which trust signals patients see. Max ${state.chip_config?.max_visible_chips || 8} chips shown. The hero preview above updates as you change these.`}
                action={
                    /* iter100.54 — Contextual "How chips work" reference,
                       pinned to the top-right of the section header. Opens
                       in a new tab so the doctor keeps their editor state. */
                    <a
                        href="/how-chips-work"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-muted-foreground hover:text-primary transition-colors"
                        data-testid="editor-trust-chips-how-chips-work"
                    >
                        How chips work
                        <ArrowSquareOut size={11} weight="bold" />
                    </a>
                }
            >
                <TrustChipsSection
                    chipConfig={state.chip_config}
                    onSaveConfig={saveChipConfig}
                    onSaveBadgeVisibility={saveBadgeVisibility}
                />
            </Section>

            {/* iter100.28 — Smile Simulator toggle removed. The AI Smile
                Preview is now enabled by default on every provider's
                public page (product decision — it's Invisique's key
                differentiator + biggest conversion driver, so opting out
                of it didn't serve doctors or Invisique). The
                `landing_show_simulator` field is retained on the doctor
                record for admin-side compliance overrides but no longer
                surfaced in the doctor editor. */}

            {/* Preview footer */}
            <div className="p-6 bg-primary/5 border border-primary/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm">
                    <div className="font-semibold">See it live</div>
                    <div className="text-muted-foreground text-xs">Your changes are already public.</div>
                </div>
                <Button asChild data-testid="editor-footer-preview-btn">
                    <a href={publicPath} target="_blank" rel="noopener noreferrer">
                        <ArrowSquareOut size={14} /> Open my page
                    </a>
                </Button>
            </div>
              </>
            )}

            {/* ============================
                REVIEWS TAB (iter100.34)
               =========================== */}
            {tab === "reviews" && <ReviewsTab />}

            {/* ============================
                CONTENT TABS (Articles / Case Studies)
               =========================== */}
            {(tab === "article" || tab === "case_study") && (
                <div className="p-6 bg-card border border-border rounded-2xl">
                    <ContentEditor kind={tab} apiBase={DOCTOR_CONTENT_API} />
                </div>
            )}
        </div>
    );
}

/* ---------- Sub-components ------------------------------------------- */

function Section({ title, hint, action, children, anchorId }) {
    return (
        <section
            className="p-6 bg-card border border-border rounded-2xl scroll-mt-24"
            id={anchorId || undefined}
            data-anchor={anchorId || undefined}
        >
            <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
                {action}
            </div>
            {hint && <p className="text-xs text-muted-foreground mt-1 mb-4">{hint}</p>}
            <div className={hint ? "" : "mt-4"}>
                {children}
            </div>
        </section>
    );
}


function TaglineField({ initial, onSave }) {
    const [v, setV] = useState(initial);
    const words = v.trim().split(/\s+/).filter(Boolean).length;
    return (
        <div>
            <Input
                value={v}
                onChange={(e) => setV(e.target.value)}
                onBlur={() => v !== initial && onSave(v.trim())}
                placeholder="e.g. Straightening Mumbai's smiles since 2012 with clear aligners"
                maxLength={120}
                data-testid="editor-tagline-input"
            />
            <div className="mt-1 flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">Aim for ~12 words</span>
                <span className={`font-mono ${words > 15 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {words} word{words === 1 ? "" : "s"}
                </span>
            </div>
        </div>
    );
}

function StoryField({ initial, onSave }) {
    const [v, setV] = useState(initial);
    const words = v.trim().split(/\s+/).filter(Boolean).length;
    // iter100.276 — Flag legacy placeholder story ("Certified Invisique
    // provider at {clinic}.") so the doctor knows they're looking at a
    // template, not their own words. Auto-generated during old admin
    // create flow.
    const isPlaceholder = /^certified invisique(™)? provider at\b/i.test(v.trim());
    return (
        <div>
            {isPlaceholder && (
                <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-[12px]" data-testid="story-placeholder-warning">
                    <strong>This intro was auto-filled by our team.</strong> Patients connect much better with your own words — take a minute to make it yours.
                </div>
            )}
            <Textarea
                value={v}
                onChange={(e) => setV(e.target.value)}
                onBlur={() => v !== initial && onSave(v.trim())}
                placeholder="I've been practising orthodontics for 12 years, and I still get excited every time a patient sees their new smile for the first time…"
                maxLength={1500}
                rows={5}
                data-testid="editor-story-input"
                className={isPlaceholder ? "border-amber-300 focus-visible:ring-amber-300" : ""}
            />
            <div className="mt-1 flex items-center justify-between text-[11px]">
                <span className="text-muted-foreground">Aim for ~150 words</span>
                <span className={`font-mono ${words > 200 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {words} word{words === 1 ? "" : "s"}
                </span>
            </div>
        </div>
    );
}

/* =========================================================================
   EDITOR IDENTITY HERO (iter100.41)
   Live mirror of the public hero card at /dr/{slug}, rendered at the top
   of the landing-page editor. Doctors see EXACTLY what patients see:
   photo · name · Invisique Certified chip · specialization · credentials
   · clinic + city · trust chips (DCI · cases · Aligner Specialist).
   Preview + Share buttons live top-right. Purely visual — no click
   interactions on the card itself; the editor tabs are the workspace.
   ========================================================================= */
function EditorIdentityHero({ slug, publicPath, doctorName, clinicName, hero, chips, saving, accent }) {
    const [shareOpen, setShareOpen] = useState(false);
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const fullUrl = slug ? `${origin}/dr/${slug}` : "";
    const h = hero || {};
    const displayName = doctorName || "Your name";
    const initial = (doctorName || "?").charAt(0).toUpperCase();
    const acc = accent || ACCENTS.teal;

    return (
        <>
            <div className="relative rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-6 bg-white" data-testid="editor-identity-hero">
                {/* Preview tag — signals to the doctor this is what patients see */}
                <div className="absolute -top-2.5 left-5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] uppercase tracking-wider font-bold">
                    <Eye size={10} weight="fill" /> Preview · what patients see
                </div>

                {/* Preview + Share buttons in the top-right */}
                <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                    {saving && <span className="text-xs text-muted-foreground pr-1" data-testid="editor-saving-indicator">Saving…</span>}
                    <Button variant="outline" size="sm" asChild data-testid="editor-preview-btn">
                        <a href={publicPath} target="_blank" rel="noopener noreferrer">
                            <Eye size={13} /> Preview
                        </a>
                    </Button>
                    <Button size="sm" onClick={() => setShareOpen(true)} data-testid="editor-share-btn">
                        <ShareNetwork size={13} /> Share
                    </Button>
                </div>

                <div className="mt-2 flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
                    {/* Photo */}
                    <div className="relative shrink-0">
                        {h.image ? (
                            <img
                                src={h.image}
                                alt={displayName}
                                className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl object-cover border-4 border-white shadow-md"
                                data-testid="hero-photo"
                            />
                        ) : (
                            <div
                                className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl border-4 border-white shadow-md flex items-center justify-center font-display text-4xl font-bold text-white"
                                style={{ backgroundColor: acc.base }}
                                data-testid="hero-photo-placeholder"
                            >
                                {initial}
                            </div>
                        )}
                        {h.dci_verified && (
                            <div className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-white shadow flex items-center justify-center border border-slate-200" title="DCI-verified provider">
                                <SealCheck size={20} weight="fill" style={{ color: acc.base }} />
                            </div>
                        )}
                    </div>

                    {/* Identity */}
                    <div className="flex-1 min-w-0 pr-0 sm:pr-32">
                        <div className="flex items-start gap-2 flex-wrap">
                            <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900 leading-tight tracking-tight truncate max-w-full" data-testid="editor-title">
                                {displayName}
                            </h1>
                            <span
                                className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider mt-1"
                                style={{ backgroundColor: acc.soft, color: acc.text }}
                                title="Certified Invisique™ Provider"
                            >
                                <Certificate size={10} weight="fill" /> Invisique™ Certified
                            </span>
                        </div>

                        {h.specialization && (
                            <div className="mt-1 text-[13px] font-medium text-slate-700 flex items-center gap-1.5" data-testid="hero-specialization">
                                <Stethoscope size={13} weight="duotone" style={{ color: acc.base }} />
                                {h.specialization}
                            </div>
                        )}

                        {/* Credentials line */}
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-600">
                            {h.qualifications && (
                                <span className="inline-flex items-center gap-1" data-testid="hero-qualifications">
                                    <GraduationCap size={12} weight="duotone" className="text-slate-500" />
                                    {h.qualifications}
                                </span>
                            )}
                            {h.years_experience ? (
                                <span className="inline-flex items-center gap-1" data-testid="hero-years">
                                    <Clock size={12} weight="duotone" className="text-slate-500" />
                                    {h.years_experience} years experience
                                </span>
                            ) : null}
                            {h.languages?.length > 0 && (
                                <span className="inline-flex items-center gap-1" data-testid="hero-languages">
                                    <Translate size={12} weight="duotone" className="text-slate-500" />
                                    {h.languages.slice(0, 3).join(", ")}
                                </span>
                            )}
                        </div>

                        {clinicName && (
                            <div className="mt-1.5 flex items-center gap-1.5 text-[12px] text-slate-600" data-testid="hero-clinic">
                                <Buildings size={12} weight="duotone" className="text-slate-500" />
                                <span className="font-medium text-slate-800">{clinicName}</span>
                                {h.city && (
                                    <>
                                        <span className="text-slate-400">·</span>
                                        <MapPin size={11} weight="duotone" className="text-slate-500" /> {h.city}
                                    </>
                                )}
                            </div>
                        )}

                        {/* iter100.163 — Chip layout mirrors the public
                            marketing page (/dr/{slug}) exactly. Verified
                            Credentials + Invisique™ Awards live inside
                            the identity column (right of photo). Only
                            In-Clinic Facilities (tier 2 practice
                            attributes) renders OUTSIDE the flex row,
                            aligned with the photo-left edge — same
                            treatment as the public marketing page. */}
                        {(() => {
                            const verifiedChips = (chips || []).filter((c) => c.source === "auto");
                            if (verifiedChips.length === 0) return null;
                            return (
                                <div className="mt-3">
                                    <div
                                        className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
                                        data-testid="editor-verified-chips-label"
                                    >
                                        Verified Credentials
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2" data-testid="editor-verified-chips">
                                        {verifiedChips.map((c) => (
                                            <PreviewTrustChip
                                                key={c.key}
                                                label={c.label}
                                                icon={<ChipIcon name={c.icon} size={13} />}
                                                tier={1}
                                                source={c.source}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                        {(() => {
                            const awardedChips = (chips || []).filter((c) => c.source === "admin");
                            if (awardedChips.length === 0) return null;
                            return (
                                <div className="mt-3">
                                    <div
                                        className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
                                        data-testid="editor-awarded-chips-label"
                                    >
                                        Invisique&trade; Awards
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2" data-testid="editor-awarded-chips">
                                        {awardedChips.map((c) => (
                                            <PreviewTrustChip
                                                key={c.key}
                                                label={c.label}
                                                icon={<ChipIcon name={c.icon} size={13} />}
                                                tier={1}
                                                source={c.source}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                        {(!chips || chips.length === 0) && (
                            <div className="mt-3 text-[11px] text-muted-foreground italic">
                                No trust chips visible — add some below.
                            </div>
                        )}
                    </div>
                </div>

                {/* iter100.163 — In-Clinic Facilities row (mirror of public page). */}
                {(() => {
                    const practiceChips = (chips || []).filter(
                        (c) => c.source !== "auto" && c.source !== "admin",
                    );
                    if (practiceChips.length === 0) return null;
                    return (
                        <div className="mt-4">
                            <div
                                className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-1.5"
                                data-testid="editor-practice-chips-label"
                            >
                                In-Clinic Facilities
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5" data-testid="editor-practice-chips">
                                {practiceChips.map((c) => (
                                    <PreviewTrustChip
                                        key={c.key}
                                        label={c.label}
                                        icon={<ChipIcon name={c.icon} size={11} />}
                                        tier={2}
                                        source={c.source}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })()}
            </div>

            <ShareProfileModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                url={fullUrl}
                title={doctorName || "Your landing page"}
                subtitle="Certified Invisique™ Provider"
                shareMessage={`See your new smile in 30 seconds — free AI preview at my page: ${fullUrl}`}
            />
        </>
    );
}

/* iter100.56 — Semantic palette for the editor preview hero chips.
   Mirrors the public microsite so the doctor sees exactly what patients
   see: auto → teal, admin → rose, doctor/derived → green. Kept as a
   local const in this file (same values as in DoctorLandingPage.jsx) to
   avoid a cross-page import; if a third surface picks it up, extract.
   iter100.163 — Extended with `softBg`/`softBorder`/`softText` so the
   tier-2 "In-Clinic Facilities" chips render exactly like the public
   page (compact rectangle, muted palette). */
const HERO_CHIP_PALETTES = {
    auto:    { bg: "hsl(192 90% 96%)", border: "hsl(192 90% 82%)", text: "hsl(192 90% 25%)", softBg: "hsl(192 55% 97%)", softBorder: "hsl(192 40% 90%)", softText: "hsl(192 55% 30%)" },
    admin:   { bg: "hsl(340 78% 97%)", border: "hsl(340 78% 88%)", text: "hsl(340 65% 38%)", softBg: "hsl(340 60% 98%)", softBorder: "hsl(340 40% 92%)", softText: "hsl(340 45% 42%)" },
    doctor:  { bg: "hsl(150 55% 96%)", border: "hsl(150 45% 82%)", text: "hsl(150 60% 28%)", softBg: "hsl(150 40% 97%)", softBorder: "hsl(150 30% 90%)", softText: "hsl(150 40% 32%)" },
    derived: { bg: "hsl(150 55% 96%)", border: "hsl(150 45% 82%)", text: "hsl(150 60% 28%)", softBg: "hsl(150 40% 97%)", softBorder: "hsl(150 30% 90%)", softText: "hsl(150 40% 32%)" },
};

/* iter100.163 — PreviewTrustChip mirrors DoctorLandingPage.jsx's
   TrustChip. Tier 1 renders a bold pill (Verified / Awards), tier 2
   renders a compact rectangle (In-Clinic Facilities). We keep a local
   copy rather than importing to avoid coupling the doctor portal
   directly to the marketing bundle. */
function PreviewTrustChip({ label, icon, tier = 1, source = "auto" }) {
    const p = HERO_CHIP_PALETTES[source] || HERO_CHIP_PALETTES.auto;
    if (tier === 2) {
        return (
            <span
                className="inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10.5px] font-medium border"
                style={{ backgroundColor: p.softBg, borderColor: p.softBorder, color: p.softText }}
                data-testid={`editor-chip-tier2-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {icon}{label}
            </span>
        );
    }
    return (
        <span
            className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[12px] font-semibold border shadow-sm"
            style={{ backgroundColor: p.bg, borderColor: p.border, color: p.text }}
            data-testid={`editor-chip-tier1-${label.toLowerCase().replace(/\s+/g, "-")}`}
        >
            {icon}{label}
        </span>
    );
}


function HeroChip({ label, icon, muted = false, title, source = "auto" }) {
    if (muted) {
        return (
            <span
                className="inline-flex items-center gap-1 h-6 px-2 rounded-md border text-[10.5px] font-semibold bg-slate-100 text-slate-500 border-slate-200"
                title={title || label}
            >
                {icon}
                {label}
            </span>
        );
    }
    const p = HERO_CHIP_PALETTES[source] || HERO_CHIP_PALETTES.auto;
    return (
        <span
            className="inline-flex items-center gap-1 h-6 px-2 rounded-md border text-[10.5px] font-semibold"
            style={{ backgroundColor: p.bg, borderColor: p.border, color: p.text }}
            title={title || label}
        >
            {icon}
            {label}
        </span>
    );
}



/* =========================================================================
   TRUST CHIPS SECTION (iter100.42)
   Doctor-facing UI to toggle chip visibility. Three column groups:
     🔵 Auto (Invisique-managed)  — locked chips + toggleable auto chips
     🟠 Awarded by Invisique      — admin-granted badges (visibility only)
     🟢 Practice attributes       — self-attested + auto-detected signals
   Every toggle triggers an optimistic mutation so the identity hero at
   the top of the page updates instantly, no reload.
   ========================================================================= */
function TrustChipsSection({ chipConfig, onSaveConfig, onSaveBadgeVisibility }) {
    if (!chipConfig) {
        return <div className="text-sm text-muted-foreground italic">Loading chip options…</div>;
    }
    const { auto_visibility, doctor_flags, computed_flags, cases_treated_count, admin_badges, taxonomies } = chipConfig;

    return (
        <div className="space-y-6" data-testid="trust-chips-section">
            {/* Bucket A · Auto (Invisique-managed) */}
            <ChipBucket
                tone="blue"
                label="Auto · Invisique-managed"
                hint="Signals we compute for you. Locked ones (Invisique™ Certified · DCI Verified) can't be hidden — they're our promise to patients."
            >
                <ChipToggleRow
                    icon="Certificate"
                    label="Invisique™ Certified"
                    hint="Every approved partner"
                    locked
                    on
                />
                <ChipToggleRow
                    icon="ShieldCheck"
                    label="DCI Verified"
                    hint="Awaiting DCI verification. Contact Invisique™ support to update."
                    locked
                    on
                    dimmedHint={!auto_visibility?.cases_treated}
                />
                <ChipToggleRow
                    icon="CheckCircle"
                    label={cases_treated_count > 0
                        ? `${cases_treated_count} Invisique™ case${cases_treated_count === 1 ? "" : "s"} treated`
                        : "Cases treated"}
                    hint={cases_treated_count > 0
                        ? "Live count — grows automatically as you complete cases."
                        : "Hidden until you have your first Invisique™ case."}
                    on={auto_visibility?.cases_treated}
                    disabled={cases_treated_count === 0}
                    onChange={(v) => onSaveConfig({ auto_visibility: { cases_treated: v } })}
                    testId="chip-toggle-cases_treated"
                />
                <ChipToggleRow
                    icon="Sparkle"
                    label="Aligner Specialist"
                    hint="Inferred from your Orthodontist specialization."
                    on={auto_visibility?.aligner_specialist}
                    onChange={(v) => onSaveConfig({ auto_visibility: { aligner_specialist: v } })}
                    testId="chip-toggle-aligner_specialist"
                />
                <ChipToggleRow
                    icon="Calendar"
                    label={<>Show &quot;Next available: [slot]&quot; on my card</>}
                    hint="Once online booking is turned on for your clinic, patients will see your next open slot right on your card (e.g. 'Next: Tue 3pm'). Turn ON to opt-in now — the chip stays hidden until real slots exist, then appears automatically. Turn OFF if you'd rather patients call before booking."
                    on={auto_visibility?.next_available}
                    onChange={(v) => onSaveConfig({ auto_visibility: { next_available: v } })}
                    testId="chip-toggle-next_available"
                />
            </ChipBucket>

            {/* Bucket B · Admin-granted */}
            <ChipBucket
                tone="amber"
                label="Awarded by Invisique™"
                hint={admin_badges?.length
                    ? "Badges Invisique™ HQ has granted you. You can hide them from your card, but only admins can grant or revoke."
                    : "Invisique™ will award badges (Featured, Panel Member, ISO 9001…) once you're eligible. Nothing granted yet."}
            >
                {(admin_badges || []).length === 0 && (
                    <div className="text-[13px] text-muted-foreground italic py-2 px-1">No badges granted yet.</div>
                )}
                {(admin_badges || []).map((b) => (
                    <ChipToggleRow
                        key={b.key}
                        icon={b.icon || "Medal"}
                        label={b.label}
                        hint={b.granted_at ? `Awarded ${new Date(b.granted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}${b.note ? ` · ${b.note}` : ""}` : ""}
                        on={b.visible !== false}
                        onChange={(v) => onSaveBadgeVisibility(b.key, v)}
                        testId={`chip-toggle-badge-${b.key}`}
                    />
                ))}
            </ChipBucket>

            {/* Bucket C · Practice attributes (doctor self-attested + auto-detected).
                iter100.58 — Switched from toggle-switch rows to click-to-toggle
                pills for consistency with the Services picker. Cleaner UI,
                fewer clicks per attribute. */}
            <ChipBucket
                tone="green"
                label="Practice attributes"
                hint="Tell patients what makes your clinic a good fit. Auto-detected signals are marked and light up automatically when you set the source data on your profile."
            >
                <PracticeAttributesPicker
                    taxonomy={taxonomies?.doctor || {}}
                    derivedTaxonomy={taxonomies?.derived || {}}
                    doctorFlags={doctor_flags || {}}
                    computedFlags={computed_flags || {}}
                    onToggle={(key, on) => onSaveConfig({ doctor_flags: { [key]: on } })}
                />
            </ChipBucket>
        </div>
    );
}

/* iter100.58 — Click-to-toggle grid for Practice attributes. Mirrors
   the visual language of the Services picker: rounded pill, icon left,
   accent state on selection. Auto-detected keys get a small "✨ Auto"
   tag and are click-disabled until the doctor sets the source data on
   their profile (matches the prior ChipToggleRow gating). */
function PracticeAttributesPicker({ taxonomy, derivedTaxonomy, doctorFlags, computedFlags, onToggle }) {
    const doctorEntries = Object.entries(taxonomy || {});
    const derivedEntries = Object.entries(derivedTaxonomy || {});
    const all = [
        ...doctorEntries.map(([k, spec]) => ({ key: k, spec, kind: "doctor", disabled: false })),
        ...derivedEntries.map(([k, spec]) => ({ key: k, spec, kind: "derived", disabled: !computedFlags?.[k] })),
    ];
    const onCount = Object.entries(doctorFlags || {}).filter(([, v]) => v).length;
    return (
        <div className="space-y-3" data-testid="practice-attributes-picker">
            <div className="text-[12px] text-muted-foreground">
                {onCount} of {all.length} picked
            </div>
            <div className="flex flex-wrap gap-2">
                {all.map(({ key, spec, kind, disabled }) => {
                    const on = !!doctorFlags?.[key];
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => !disabled && onToggle(key, !on)}
                            disabled={disabled}
                            title={disabled ? "Not detected on your profile yet — set the source data to enable." : undefined}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-medium border transition-colors ${
                                on
                                    ? "bg-primary/10 border-primary/40 text-primary"
                                    : disabled
                                        ? "bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed"
                                        : "bg-white border-slate-200 text-slate-700 hover:border-primary/50 hover:text-primary"
                            }`}
                            data-testid={`practice-attr-${key}`}
                            aria-pressed={on}
                        >
                            <ChipIcon name={spec.icon} size={12} />
                            {spec.label}
                            {kind === "derived" && (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-white text-slate-500 border border-slate-200 px-1 rounded ml-1">
                                    Auto
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* iter100.55 — Services picker: click a pill to toggle.
   iter100.58 — Cap removed; renders every taxonomy option and lets the
   doctor pick as many as they legitimately offer. */
function ServicesPicker({ taxonomy, selected, onToggle }) {
    const selectedSet = new Set(selected);
    return (
        <div className="space-y-3" data-testid="services-picker">
            <div className="text-[12px] text-muted-foreground">
                {selectedSet.size} of {taxonomy.length} picked
            </div>
            <div className="flex flex-wrap gap-2">
                {taxonomy.map((s) => {
                    const on = selectedSet.has(s.key);
                    return (
                        <button
                            key={s.key}
                            type="button"
                            onClick={() => onToggle(s.key)}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[13px] font-medium border transition-colors ${
                                on
                                    ? "bg-primary/10 border-primary/40 text-primary"
                                    : "bg-white border-slate-200 text-slate-700 hover:border-primary/50 hover:text-primary"
                            }`}
                            data-testid={`services-picker-${s.key}`}
                            aria-pressed={on}
                        >
                            <CheckCircle
                                size={12}
                                weight={on ? "fill" : "regular"}
                                className={on ? "text-primary" : "text-slate-300"}
                            />
                            {s.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* Chip bucket wrapper — colored dot + label + hint + row list */
function ChipBucket({ tone, label, hint, children }) {
    const dotColor = {
        blue:  "bg-primary",
        amber: "bg-amber-500",
        green: "bg-emerald-500",
    }[tone] || "bg-slate-400";
    return (
        <div className="rounded-xl border border-border bg-white p-4">
            <div className="flex items-baseline gap-2 flex-wrap mb-1">
                <span className={`inline-block h-2 w-2 rounded-full ${dotColor}`} />
                <h3 className="text-[13px] font-bold text-slate-900">{label}</h3>
            </div>
            {hint && <p className="text-[11.5px] text-muted-foreground mb-3">{hint}</p>}
            <div className="divide-y divide-slate-100">{children}</div>
        </div>
    );
}

/* Single toggle row: icon · label · hint · switch (or locked pill) */
function ChipToggleRow({ icon, label, hint, on, locked = false, disabled = false, onChange, testId }) {
    return (
        <div
            className={`flex items-center gap-3 py-2.5 ${disabled && !locked ? "opacity-50" : ""}`}
            data-testid={testId}
        >
            <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${on ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-400"}`}>
                <ChipIcon name={icon} size={13} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-800">{label}</div>
                {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
            </div>
            {locked ? (
                <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-slate-100 border border-slate-200 text-[10.5px] font-semibold text-slate-600" title="Locked — always shown">
                    <ShieldCheck size={10} weight="fill" /> Locked
                </span>
            ) : (
                <button
                    type="button"
                    role="switch"
                    aria-checked={!!on}
                    onClick={() => !disabled && onChange?.(!on)}
                    disabled={disabled}
                    className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${on ? "bg-primary" : "bg-slate-200"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                    data-testid={testId ? `${testId}-switch` : undefined}
                >
                    <span
                        className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${on ? "translate-x-4" : "translate-x-0.5"}`}
                    />
                </button>
            )}
        </div>
    );
}


/* =========================================================================
   REVIEWS TAB (iter100.34)
   Verified Patient Reviews · Coming Soon.
   Content moved from the deleted /doctor/reviews standalone page into
   this tab. Explains the model (verified-by-booking, WhatsApp trigger,
   auto-published) and shows a sample review card so doctors know what
   to expect once the pipeline is live.
   ========================================================================= */
function ReviewsTab() {
    const HIGHLIGHTS = [
        {
            icon: ShieldCheck,
            title: "Verified by booking",
            body: "Every review is tied to a completed Invisique™ appointment — no anonymous or synthetic reviews.",
        },
        {
            icon: WhatsappLogo,
            title: "Zero effort from you",
            body: "3-5 days after each visit, the patient gets a friendly WhatsApp asking them to rate their experience.",
        },
        {
            icon: Sparkle,
            title: "Auto-published to your page",
            body: "Approved reviews appear on your public profile with a verified badge — no writing needed.",
        },
    ];

    return (
        <div className="space-y-8" data-testid="reviews-tab">
            {/* Hero card — the "Coming soon" identity */}
            <section
                className="relative overflow-hidden rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8"
                data-testid="reviews-hero-card"
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="h-11 w-11 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                        <ChatCircleText size={22} weight="duotone" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 flex items-center gap-1">
                            <Clock size={11} weight="fill" /> Rolling out soon
                        </div>
                        <h2 className="font-display text-xl font-bold text-slate-900">Your verified patient reviews</h2>
                    </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed max-w-xl">
                    Once the feature is live, this tab will show every star rating and comment left by your Invisique™ patients, along with a public preview of how they look on your landing page.
                </p>

                {/* Sample review card */}
                <div
                    className="mt-6 rounded-xl border border-border bg-white/80 backdrop-blur px-5 py-4 shadow-sm relative"
                    data-testid="reviews-preview-mock"
                >
                    <div className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900 text-white text-[10px] uppercase tracking-wider font-bold">
                        <Sparkle size={10} weight="fill" /> Preview
                    </div>
                    <div className="flex items-center gap-1 text-amber-500 mb-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <Star key={n} size={16} weight="fill" />
                        ))}
                    </div>
                    <p className="text-sm text-slate-800 italic leading-snug">
                        &ldquo;Dr. was patient, explained every step, and my aligners were ready in a week. My smile looks natural — no one can tell I&apos;m in treatment.&rdquo;
                    </p>
                    <div className="mt-3 flex items-center justify-between">
                        <div className="text-xs text-muted-foreground">
                            <span className="font-semibold text-slate-900">R. Sharma</span> · verified patient
                        </div>
                        <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                            <ShieldCheck size={11} weight="fill" /> Verified
                        </div>
                    </div>
                </div>
            </section>

            {/* Why this model */}
            <section>
                <h2 className="text-lg font-display font-bold mb-3">Why we&apos;re building it this way</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
                        <div
                            key={title}
                            className="p-5 bg-card border border-border rounded-2xl"
                            data-testid={`reviews-highlight-${title.toLowerCase().replace(/\s/g, "-")}`}
                        >
                            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                                <Icon size={20} weight="duotone" />
                            </div>
                            <div className="font-display font-bold text-base text-slate-900 mb-1">{title}</div>
                            <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

